/**
 * The overlay provider is used to manage screen overlays such as Flyouts or modals, which are use to display content in a dynamic fashion based on the directive from the amplience request
 *
 * @module views/components/OverlayProvider
 * @memberof -Common
 */
import './OverlayProvider.scss';

import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import deepmerge from 'deepmerge';

import Confetti from '@ulta/core/components/Confetti/Confetti';
import OverlayModal from '@ulta/core/components/OverlayModal/OverlayModal';
import DevPanel_ApolloStateSplitPane from '@ulta/core/devpanel/src/components/DevPanel_ApolloStateSplitPane/DevPanel_ApolloStateSplitPane';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Dimmer from '../../components/Dimmer/Dimmer';
import DynamicOverlayComponent from '../../components/DynamicOverlayComponent/DynamicOverlayComponent';
import OverlayBottomSheet from '../../components/OverlayBottomSheet/OverlayBottomSheet';
import OverlayFlyout from '../../components/OverlayFlyout/OverlayFlyout';
import useLoader from '../../hooks/useLoader/useLoader';
import { useNextRender } from '../../hooks/useNextRender/useNextRender';
import { handleDXLNavigationType, processClientActionType, processCloseAction } from '../../utils/clientActionProcessor/clientActionProcessor';
import { DXL_NAVIGATION_TYPE } from '../../utils/constants/action';
import { isServer } from '../../utils/device_detection/device_detection';
import { devLogger } from '../../utils/devMode/devMode';
import { handleEmptyObjects } from '../../utils/handleEmptyObjects/handleEmptyObjects';
import { isFunction } from '../../utils/types/types';
import { useLayerHostContext } from '../LayerHostProvider/LayerHostProvider';
import { usePageDataContext } from '../PageDataProvider/PageDataProvider';
import { useUserContext } from '../UserContextProvider/UserContextProvider';
import { getDialogPolyfill } from './getDialogPolyfill';
import * as utils from './OverlayProvider';

/**
 * Represents a OverlayProvider component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns OverlayProvider
 */
export const OverlayProvider = function( props ){
  const overlayRef = useRef();
  const userCancelled = useRef( false );
  const isClosingRef = useRef( false );
  const [isClosing, setIsClosing] = useState( false );
  const overlayOptions = useRef( {} );
  const scrollYRef = useRef();
  const postOverlayTeardown = useRef( null );

  const [state, dispatch] = useReducer( overlayProviderReducer, configInitialState );
  const [confetti, setConfetti] = useState( null );
  const [scheduleNextRender] = useNextRender();
  const devMode = utils.useDevMode();
  const { dxlRequestStack } = useLayerHostContext();
  const { user } = useUserContext();

  const {
    alignment,
    ariaLabel,
    backAction,
    backActionCallback,
    closeAccessibilityLabel,
    content,
    crossButtonVisibility,
    customClassName,
    disableClose,
    displayOverlay,
    isSwatchModal,
    isVideo,
    offsetElement,
    closeAction,
    onClose,
    opener,
    showDynamicFlyoutHeader,
    type,
    videoMeta,
    subtitle,
    title,
    headerText,
    customCloseEvent,
    supportText,
    variant,
    ariaLabelledby,
    renderAsModalOnAllViewports,
    heightPercentageOverride
  } = state;

  // Sync isClosingRef with isClosing state
  useEffect( () => {
    isClosingRef.current = isClosing;
  }, [isClosing] );

  // Keep a stable reference to overlay options we need in helpers
  useEffect( () => {
    overlayOptions.current = { ...state };
  }, [displayOverlay, type, alignment, disableClose] );

  const closeActionModel = useRef( getInitialCloseActionModel( { closeAction, closeActionModel: CLOSE_ACTION_MODEL } ) );

  const [loader, showLoader, hideLoader] = useLoader();

  /*
   * Callbacks
   *
   * -----------
   * public API:
   * -----------
   *
   * openOverlay()
   *    - accepts arguments from compenents and dispatches them to the reducer
   *
   * closeOverlay():
   *    - will initatiate dialog.close()
   *    - this is the public API method used by components
   *
   * updateContent():
   *    - components can call this to update the contents of an overlay
   *
   * updateOverlay():
   *    - components can call this to update the props
   *
   * updateBackAction():
   *    - components can call this to update the back action in the header
   *
   * updateCloseAction():
   *    - components can call this to update the on close DXL actions that need to execute
   *
   * -------------
   * internal API:
   * -------------
   *
   * onDialogClose():
   *   - happens when dialog.close() is called,
   *   - manages transitions
   *   - waits for close actions to run
   *   - dispatches to the reducer
   *
   *
   * onCancelClose();
   *    - listens for click on the 'X' icon in overlay flyout header and calls closeOverlay()
   *
   * onDisplayStateChange():
   *    - listens to display state and manages DOM effects
   *    - TODO: We need to take a look at when close actions lag, the scroll block will
   *            not be removed until the action is done processing. Should ask UX what
   *            the expectation is when a close action request lags/timesout.
   */

  // Handles executing the closing of the overlay
  const closeOverlay = useCallback( utils.composeOnClose( { overlayRef, userCancelled } ), [overlayRef] );

  // Handles closing the overlay when the dimmer is clicked
  const onDimmerClose = useCallback( utils.composeOnDimmerClose( { closeOverlay, disableClose } ), [closeOverlay, disableClose] );

  // Handles opening the overlay
  const openOverlay = useCallback( utils.composeOnOpenOverlay( { dispatch } ), [dispatch] );

  // Handles the native on close event
  const onDialogClose = useCallback(
    utils.composeOnDialogClose(
      { opener, overlayRef, type, closeActionModel, userCancelled, customCloseEvent, postOverlayTeardown, isClosingRef, user },
      { dispatch, openOverlay, showLoader, hideLoader, onClose, scheduleNextRender, setIsClosing, closeOverlay }
    ),
    [opener, postOverlayTeardown, overlayRef, type, dispatch, scheduleNextRender, user, closeOverlay]
  );

  // Handles updating the content inside the overlay dynamically
  const updateContent = useCallback( utils.composeUpdateContent( { dispatch } ), [dispatch] );

  // Handles updating the aria-label inside the overlay dynamically
  const updateOverlay = useCallback( utils.composeUpdateOverlay( { dispatch } ), [dispatch] );

  // Handles updating the back action dynamically
  const updateBackAction = useCallback( utils.composeUpdateBackAction( { dispatch } ), [dispatch] );

  const updateCloseAction = useCallback( utils.composeUpdateCloseAction( { closeActionModel } ), [closeActionModel] );

  // Handles showing confetti
  const showConfetti = useCallback( utils.composeShowConfetti( { setConfetti } ), [setConfetti] );

  // Handles hiding confetti
  const hideConfetti = useCallback( utils.composeHideConfetti( { setConfetti } ), [setConfetti] );

  const setPostOverlayTeardown = useCallback( utils.composeSetPostOverlayTeardown( { postOverlayTeardown } ), [] );

  // Handles open/closing state changes
  const onDisplayStateChange = useCallback(
    utils.composeOnDisplayStateChange(
      { scrollYRef, displayOverlay, overlayRef, userCancelled, overlayOptions, postOverlayTeardown },
      { closeOverlay, onClose, onDialogClose }
    ),
    [displayOverlay, overlayRef, userCancelled, closeOverlay, onClose]
  );

  useEffect( onDisplayStateChange, [onDisplayStateChange] );

  // Handles auto closing when we lose a session
  utils.useCloseOnSessionExpiration( { updateContent, closeOverlay } );

  const { breakpoint } = useDeviceInflection();
  const {
    displayBottomSheet,
    displayFlyout,
    displayModal
  } = utils.getOverlayType( { displayOverlay, type, breakpoint, isVideo, renderAsModalOnAllViewports } );

  const { windowSize } = useDeviceInflection();

  return (
    <OverlayContext.Provider
      value={ {
        backAction,
        backActionCallback,
        closeOverlay,
        crossButtonVisibility,
        displayOverlay,
        hidePageLoader: hideLoader,
        isOverlay,
        isClosingRef,
        onDialogClose,
        openOverlay,
        closeAccessibilityLabel,
        type,
        showDynamicFlyoutHeader,
        showPageLoader: showLoader,
        updateBackAction,
        updateContent,
        updateCloseAction,
        showConfetti,
        hideConfetti,
        updateOverlay,
        offsetElement,
        overlayRef,
        setPostOverlayTeardown
      } }
    >
      { props.children }

      { devMode && (
        <div className='DevPanelWrapper'>
          <DevPanel_ApolloStateSplitPane />
        </div>
      ) }

      { displayBottomSheet && (
        <OverlayBottomSheet
          ref={ overlayRef }
          ariaLabel={ ariaLabel }
          closeAccessibilityLabel={ closeAccessibilityLabel }
          customClassName={ customClassName }
          disableClose={ disableClose }
          onClose={ onDialogClose }
          title={ title }
          subtitle={ subtitle }
          ariaLabelledBy={ ariaLabelledby }
          supportText={ supportText }
          variant={ variant }
          heightPercentageOverride={ heightPercentageOverride }
        >
          { content }
        </OverlayBottomSheet>
      ) }

      { displayModal && (
        <OverlayModal
          isVideoModal={ isVideo }
          videoMeta={ videoMeta }
          isSwatchModal={ isSwatchModal }
          ref={ overlayRef }
          ariaLabel={ ariaLabel }
          closeAccessibilityLabel={ closeAccessibilityLabel }
          customClassName={ customClassName }
          onClose={ onDialogClose }
          disableClose={ disableClose }
        >
          { content }
        </OverlayModal>
      ) }

      { displayFlyout && (
        <OverlayFlyout
          { ...spreadProps( {
            backAction,
            backActionCallback,
            overlayType: OVERLAY_TYPE.Flyout,
            showDynamicFlyoutHeader: showDynamicFlyoutHeader,
            offsetElement: offsetElement,
            crossButtonVisibility: crossButtonVisibility
          } ) }
          ref={ overlayRef }
          alignment={ alignment }
          ariaLabel={ ariaLabel }
          closeAccessibilityLabel={ closeAccessibilityLabel }
          customClassName={ customClassName }
          onClose={ onDialogClose }
          disableClose={ disableClose }
          headerText={ headerText }
          customCloseEvent={ customCloseEvent }
        >
          { content }
        </OverlayFlyout>
      ) }

      { !!loader && (
        <div className='OverlayLoader'>{ loader }</div>
      ) }
      { confetti && Object.keys( confetti ).length > 0 && (
        <div className='OverlayConfettiWrapper'>
          <Confetti
            { ...spreadProps( { ...confetti } ) }
            { ...spreadProps( {
              width: windowSize?.width,
              height: windowSize?.height
            } ) }
            onConfettiComplete={ () => setConfetti( null ) }
          />
        </div>
      ) }

      { displayOverlay && !isServer() && createPortal(
        <Dimmer
          onClick={ !disableClose ? onDimmerClose : undefined }
          className={ utils.getBackdropClassName( { type, isClosing, customClassName } ) }
          zIndex={ constants.OVERLAY_BACKDROP_Z_INDEX }
        />,
        document.body
      ) }
    </OverlayContext.Provider>
  );
};

/**
 * @function useCloseOnSessionExpiration
 * @description Closes or updates overlay when we lose a session
 * @param {object} methods - methods
 * @param {function} methods.updateContent - updates overlay content
 * @param {function} methods.closeOverlay - closes overlay
 */
export const useCloseOnSessionExpiration = ( methods ) => {
  const { updateContent, closeOverlay } = methods || {};
  const { user } = useUserContext();
  const prevUser = useRef( user );
  const { data } = usePageDataContext();

  useEffect( () => {
    if( prevUser.current.loginStatus === user.loginStatus ){
      return;
    }

    const lostSession = prevUser.current.loginStatus && !user.loginStatus;
    const { loginAction } = data?.Page?.meta || {};

    if( lostSession && !loginAction ){
      closeOverlay();
    }
    else if( lostSession ){
      updateContent( <DynamicOverlayComponent action={ loginAction } /> );
    }

    prevUser.current = user;
  }, [user.loginStatus] );
};

/**
 * @function composeOnDisplayStateChange
 * @description Represents a composeOnDisplayStateChange method,which calls on change Display State
 * @param {object} data - Arguments
 * @param {boolean} data.displayOverlay - State of the overlay, open/close
 * @param {object} data.overlayRef - Ref to the overlay element
 * @param {object} methods - Methods
 * @param {function} methods.closeOverlay - Handles closing the overlay
 * @param {function} methods.onClose - Handles the onClose events
 */
export const composeOnDisplayStateChange = ( data, methods ) => () => {
  const { scrollYRef = {}, displayOverlay, overlayRef = {}, userCancelled = {}, overlayOptions = {}, postOverlayTeardown } = handleEmptyObjects( data );
  const { closeOverlay, onClose, onDialogClose } = methods || {};
  const { type } = handleEmptyObjects( overlayOptions.current );
  if( !isServer() && displayOverlay && typeof HTMLDialogElement !== 'function' && overlayRef.current ){
    getDialogPolyfill().then( ( registerDialog ) => {
      registerDialog( overlayRef.current );
      overlayRef.current.showModal();
      overlayRef.current.addEventListener( 'close', onDialogClose );
    } );
  }

  // Manages open side effects
  if( displayOverlay ){
    // Setting the current scroll posoiton to body
    if( !scrollYRef?.current ){
      scrollYRef.current = global.scrollY;
    }
    const styleTop = `-${scrollYRef.current}px`;
    global.document.body.style.top = styleTop;

    overlayRef?.current?.showModal?.();
    // if the overlay is open then block page scrolling
    const overlaySetup = () => {
      if( !overlayRef.current ){
        return;
      }

      global.document.body.classList.add( OVERLAY_SCROLL_BLOCK_CLASS );
      overlayRef.current.removeEventListener( 'transitionend', overlaySetup );
    };
    if( type.toLowerCase() === OVERLAY_TYPE.Modal.toLowerCase() || type.toLowerCase() === OVERLAY_TYPE.BottomSheet.toLowerCase() ){
      overlaySetup();
    }
    else {
      overlayRef.current.addEventListener( 'transitionend', overlaySetup );
    }

    // Setup event listeners
    document.addEventListener( OVERLAY_CLOSE_EVENT, closeOverlay );
    return;
  }

  // Manages close side effects
  const styleTop = `-${scrollYRef.current}px`;
  global.document.body.classList.remove( OVERLAY_SCROLL_BLOCK_CLASS );
  global.document.body.style.top = '';
  scrollYRef.current = global.scrollY;

  // Restoring the scroll positon which was set earlier
  global.scrollTo( 0, parseInt( styleTop || '0', 10 ) * -1 );

  // Remove event listeners
  document.removeEventListener( OVERLAY_CLOSE_EVENT, closeOverlay );

  if( isFunction( postOverlayTeardown?.current ) ){
    executePendingAction( { postOverlayCloseProcess: postOverlayTeardown.current } );
    postOverlayTeardown.current = null; // Clear the action after execution
  }

  // On close callback
  if( isFunction( onClose ) ){
    onClose( {
      // Lets the caller know if this was a user cancel or programmatic/success close
      userCancelled: !!userCancelled.current
    } );
  }

  // Reset userCancelled status
  userCancelled.current = false;
};

/**
 * @method composeOnDimmerClose
 * @summary Returns a click handler for the Dimmer backdrop. When not disableClose, calls closeOverlay with userCancelled.
 * @param {object} methods - Object which contains closeOverlay and disableClose.
 * @returns {function} Click handler to pass to Dimmer onClick.
 */
export const composeOnDimmerClose = ( methods ) => ( e ) => {
  const { closeOverlay, disableClose } = methods || {};
  if( disableClose ){
    return;
  }
  e?.stopPropagation();
  closeOverlay( { userCancelled: true } );
};

/**
 * @function composeOnOpenOverlay
 * @description Represents a composeOnOpenOverlay method, which calls from openOverlay
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 */
export const composeOnOpenOverlay = ( methods ) => ( overlayParams ) => {
  const { dispatch } = methods || {};

  if( !isFunction( dispatch ) ){
    return;
  }
  dispatch( { type: OVERLAY_ACTIONS.OPEN_OVERLAY, payload: overlayParams } );
};

/**
 * @function composeOnClose
 * @description Represents a composeOnClose method, which calls from onCloseOverlay
 * @param {object} data - Argument
 * @param {object} data.overlayRef - Ref to the overlay element
 */
export const composeOnClose = ( data ) => ( eventData ) => {
  const { overlayRef, userCancelled } = data || {};

  if( !isFunction( overlayRef?.current?.close ) ){
    return;
  }

  // Allows programmatic callers to determine if this is a user cancel or on success close
  if( typeof eventData?.userCancelled === 'boolean' ){
    userCancelled.current = eventData.userCancelled;
  }

  overlayRef.current.close();
};

/**
 * @const {object} CLOSE_ACTION_MODEL - Base model for close action
 */
export const CLOSE_ACTION_MODEL = {
  data: {
    action: undefined,
    loading: undefined,
    reOpen: false,
    disableClose: false
  },
  methods: {}
};

/**
 * @function getInitialCloseActionModel
 * @description Represents a getInitialCloseActionModel method
 *
 * @param {Object} data - Arguments
 * @param {string} data.closeAction - close action state
 * @param {Object} data.closeActionModel - Ref for the close action model
 * @returns {Object} - The updated close action model.
 */
export const getInitialCloseActionModel = ( data ) => {
  const { closeAction, closeActionModel } = data || {};
  const model = { ...closeActionModel };

  if( closeAction ){
    closeActionModel.data.action = closeAction;
  }

  return model;
};

/**
 * @function composeOnDialogClose
 * @description Represents a composeOnDialogClose method
 * @param {object} data - Arguments
 * @param {object} data.opener - Ref to the element that opened the overlay
 * @param {object} data.overlayRef - Ref to the overlay element
 * @param {object} data.closeActionModel - Ref for the close action model
 * @param {object} data.userCancelled - Ref that maintains whether or not this was a user cancel (click outside/X click)
 * @param {string} data.type - Type of overlay
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 * @param {function} methods.showLoader - Show page loader
 * @param {function} methods.hideLoader - Hide page loader
 * @param {function} methods.scheduleNextRender - scheduleNextRender
 * @param {function} methods.setIsClosing - setIsClosing state setter
 */
export const composeOnDialogClose = ( data, methods ) => ( event, eventData ) => {
  const { opener = {}, isClosingRef = {}, overlayRef = {}, type, closeActionModel = {}, userCancelled = {}, customCloseEvent, postOverlayTeardown, user } = handleEmptyObjects( data );
  const { dispatch, openOverlay, showLoader, hideLoader, onClose, scheduleNextRender, setIsClosing, closeOverlay } = methods || {};

  if( !overlayRef.current || !type ){
    return;
  }

  isClosingRef.current = true;
  setIsClosing && setIsClosing( true );
  event?.stopPropagation();

  // Allows programmatic callers to determine if this is a user cancel or on success close
  if( typeof eventData?.userCancelled === 'boolean' ){
    userCancelled.current = eventData.userCancelled;
  }

  const overlayTeardown = () => {
    overlayRef.current?.removeEventListener( 'transitionend', overlayTeardown );
    setIsClosing && setIsClosing( false ); // reset the closing state after the animation ends

    utils.onOverlayTeardown(
      { opener, isClosingRef, closeActionModel, userCancelled: userCancelled, postOverlayTeardown, user },
      { dispatch, openOverlay, showLoader, hideLoader, scheduleNextRender, setIsClosing, closeOverlay }
    );
  };

  // Display refresh action loader as soon as the flyout/modal starts to close
  if( closeActionModel.current?.data?.action?.graphql ){
    showLoader();
  }

  if( type === OVERLAY_TYPE.Modal || type === OVERLAY_TYPE.BottomSheet ){
    overlayTeardown();
  }
  else if( customCloseEvent ){
    /** handle the transition end event for custom close UI.
     * the issue for the `'transitionend'` event is being attached to the entire overlay container (`overlayRef.current`). If any child element (like a button) inside the overlay triggers a CSS transition (e.g., on hover/focus), the `'transitionend'` event bubbles up to the overlay, causing `overlayTeardown()` to run and the overlay to close unexpectedly by overlayRef.current.addEventListener('transitionend', overlayTeardown);
     */
    overlayRef.current.addEventListener( 'transitionend', function handler( e ){
      if( e.target === overlayRef.current ){
        overlayTeardown();
      }
    } );
  }
  else {
    /** transition end event for overlay without the custom close UI */
    overlayRef.current.addEventListener( 'transitionend', overlayTeardown );
  }

  // On close callback
  if( isFunction( onClose ) ){
    onClose( {
      // Lets the caller know if this was a user cancel or programmatic/success close
      userCancelled: !!userCancelled.current
    } );
  }
};

/**
 * Executes a pending action stored in a ref, typically after an overlay has finished closing.
 * This allows for deferred actions, such as opening a new overlay, to occur after the previous one is fully torn down.
 * @param {object} data - Data containing the overlay teardown function
 * @param {function} data.postOverlayTeardown - Function to run after overlay teardown
 * @returns {promise} A promise that resolves when the action has been executed, or immediately if there is no action.
 */
export const executePendingAction = async( data ) => {
  const { postOverlayCloseProcess } = handleEmptyObjects( data );

  if( !isFunction( postOverlayCloseProcess ) ){
    return;
  }

  await postOverlayCloseProcess();
};

/**
 * @function onOverlayTeardown
 * @description Helper to cleanup/execute post close tasks
 *
 * @param {object} data - Arguments
 * @param {object} data.opener - Ref to the element that opened the overlay
 * @param {object} data.closeActionModel - Ref for the close action model
 * @param {object} data.userCancelled - Ref that maintains whether or not this was a user cancel (click outside/X click)
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 * @param {function} methods.showLoader - Show page loader
 * @param {function} methods.hideLoader - Hide page loader
 * @param {function} methods.scheduleNextRender - scheduleNextRender
 */
// We use a ref here because processCloseAction is currently polling the loading
// status of a potential close action so teardown can await before removing the
// full page loader.
//
// The flow of a close action is:
// 1. user closes via ESC, X, or outside click
// 2. Because DXL sends close and back actions to child modules, we have workarounds
//    that are managed outside our normal architecture. This is a stop gap until Overlay Wizard
//    is complete and properly modeled.
// 3. From the child component, the update close action helper updates this ref
// 4. Inside processCloseAction we poll the ref's loading keys to return Promise.resolve or not
// 5. This way, the async tear down function can then manage animating the close, blocking the
//    page w/ a full screen loader, then removing the loader when the close action is done.
export const onOverlayTeardown = async( data, methods ) => {
  const { opener = {}, isClosingRef = {}, closeActionModel = {}, userCancelled = {}, postOverlayTeardown, user } = handleEmptyObjects( data );
  const { dispatch, openOverlay, showLoader, hideLoader, scheduleNextRender, closeOverlay } = methods || {};
  const hasPostOverlayCloseAction = isFunction( postOverlayTeardown?.current );
  if( !dispatch || !showLoader || !hideLoader ){
    return;
  }

  // If user cancelled the modal AND disable close is true, do nothing.
  if( userCancelled?.current && closeActionModel?.current?.data?.disableClose ){
    // Reset userCancelled status for next click
    userCancelled.current = false;

    return;
  }

  showLoader();

  devLogger( {
    title: '[OverlayProvider] processing closeAction | START',
    value: { data: closeActionModel.current?.data, closeActionModel, userCancelled }
  } );

  await processCloseAction( { closeActionModel, userCancelled, hasPostOverlayCloseAction } );

  const { action } = closeActionModel.current?.data || {};

  if( userCancelled.current && action?.clientActionType ){
    processClientActionType( { action, user }, { closeOverlay } );
  }

  devLogger( {
    title: '[OverlayProvider] processing closeAction | END',
    value: { data: closeActionModel.current?.data, closeActionModel, userCancelled }
  } );

  hideLoader();
  isClosingRef.current = false;

  // If we've received another overlay as the response for a close action, we can open it here
  if( isOverlay( closeActionModel.current?.data?.reOpenAction?.navigationType ) ){
    devLogger( '[OverlayProvider] closeAction returned another overlay', 1 );

    // We need to reset before opening the new overlay
    dispatch( { type: OVERLAY_ACTIONS.CLOSE_OVERLAY } );

    const action = { ...closeActionModel.current.data.reOpenAction };

    handleDXLNavigationType( { action }, { openOverlay } );

    closeActionModel.current = { ...CLOSE_ACTION_MODEL };

    return;
  }

  // Reset close action model
  closeActionModel.current = { ...CLOSE_ACTION_MODEL };

  dispatch( { type: OVERLAY_ACTIONS.CLOSE_OVERLAY } );

  // Added for Safari - page was scrolling to bottom on overlay close
  scheduleNextRender( () => opener.current?.focus?.() );
};

/**
 * @function composeUpdateContent
 * @description Represents a composeUpdateOverlay method which trigger dispatch method
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 * @returns {updateContent}
 */
export const composeUpdateContent = ( methods ) => ( content ) => {
  const { dispatch } = methods || {};

  if( !dispatch ){
    return;
  }

  dispatch( { type: OVERLAY_ACTIONS.UPDATE_OVERLAY, payload: { content: content } } );
};

/**
 * @function composeUpdateOverlay
 * @description Represents a composeUpdateOverlay method which trigger dispatch method
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 * @returns {updateOverlay}
 */
export const composeUpdateOverlay = ( methods ) => ( content ) => {
  const { dispatch } = methods || {};

  if( !dispatch ){
    return;
  }

  dispatch( { type: OVERLAY_ACTIONS.UPDATE_OVERLAY, payload: content } );
};

/**
 * @function composeUpdateBackAction
 * @description Represents a composeUpdateBackAction method which trigger dispatch method
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 * @returns {updateBackAction} - Update back action callback
 */
export const composeUpdateBackAction = ( methods ) => ( backData, backMethods ) => {
  const { dispatch } = methods || {};

  if( !dispatch ){
    return;
  }

  const { backAction } = backData || {};
  const { callback: backActionCallback } = backMethods || {};
  const payload = { backAction, backActionCallback };

  dispatch( { type: OVERLAY_ACTIONS.UPDATE_BACK_ACTION, payload } );
};

/**
 * @function composeUpdateCloseAction
 * @description Represents a composeUpdateCloseAction method which trigger dispatch method
 * @param {object} modelData - Arguments
 * @param {{ current: object }} modelData.closeActionModel - closeActionModel ref
 * @returns {updateBackAction} - Update back action callback
 */
export const composeUpdateCloseAction = ( modelData ) => ( data, methods ) => {
  const { closeActionModel } = modelData || {};

  if( !closeActionModel?.current ){
    return;
  }

  devLogger( { title: '[OverlayProvider] updating closeAction', value: data } );

  closeActionModel.current = deepmerge( closeActionModel.current, { data, methods } );
};

/**
 * @function composeShowConfetti
 * @description Creates a function to show confetti by updating the overlay state.
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 */
export const composeShowConfetti = ( methods ) => ( confettiProps ) => {
  const { setConfetti } = methods || {};

  if( !isFunction( setConfetti ) ){
    return;
  }

  setConfetti( {
    run: true,
    recycle: false,
    ...confettiProps
  } );
};

/**
 * @function composeHideConfetti
 * @description Creates a function to hide confetti by clearing it from the overlay state.
 * @param {object} methods - Methods
 * @param {function} methods.dispatch - Overlay reducer dispatch method
 */
export const composeHideConfetti = ( methods ) => () => {
  const { setConfetti } = methods || {};

  if( !isFunction( setConfetti ) ){
    return;
  }
  setConfetti( null );
};

/**
 * @function composeSetPostOverlayTeardown
 * @description Creates a function to set a pending action that will be executed after the overlay closes.
 * @param {object} data - Arguments
 * @param {{ current: function }} data.postOverlayTeardown - Ref to store the pending action.
 * @returns {function(function): void} - A function that takes an action and sets it as the pending action.
 */
export const composeSetPostOverlayTeardown = ( data ) => ( action ) => {
  const { postOverlayTeardown } = data || {};
  if( !postOverlayTeardown ){
    return;
  }
  postOverlayTeardown.current = action;
};


/**
 * @typedef {object} OverlayConfig
 * @property {string} ariaLabel Aria label
 * @property {object} backAction DXL back action
 * @property {function} backActionCallback Client side callback on back click
 * @property {{ label: string }} closeAccessibilityLabel
 * @property {boolean} crossButtonVisibility crossButtonVisibility show cross button
 * @property {string} customClassName customClassName decorate wrapper with class
 * @property {boolean} displayOverlay enables rendering the overlay,
 * @property {boolean} isSwatchModal
 * @property {boolean} isVideo
 * @property {string} alignment alignment of a flyout
 * @property {JSX.Element} content React element to display in the overlay,
 * @property {string} type type,
 * @property {function} onClose called on Close
 * @property {{current: object}} opener React ref to store element that initiated the overlay
 * @property {object} offsetElement Ref of HTML element to automatically get top offset from
 * @property {boolean} showDynamicFlyoutHeader showDynamicFlyoutHeader display flyout header in OverlayContainer
 * @property {{ width: number, height: number }} videoMeta
 */

/**
 * @type {OverlayConfig}
 */
export const configInitialState = {
  alignment: 'right',
  ariaLabel: undefined,
  backAction: undefined,
  backActionCallback: undefined,
  closeAccessibilityLabel: '',
  content: undefined,
  crossButtonVisibility: true,
  customClassName: '',
  disableClose: false,
  displayOverlay: false,
  isSwatchModal: false,
  isVideo: false,
  offsetElement: undefined,
  onClose: undefined,
  opener: undefined,
  showDynamicFlyoutHeader: false,
  type: undefined,
  videoMeta: '',
  title: undefined,
  subtitle: undefined,
  headerText: undefined,
  customCloseEvent: undefined,
  renderAsModalOnAllViewports: false,
  heightPercentageOverride: undefined
};

/**
 * @const {object} OVERLAY_ACTIONS - Overlay reducer action types
 */
export const OVERLAY_ACTIONS = {
  CLOSE_OVERLAY: 'CLOSE_OVERLAY',
  OPEN_OVERLAY: 'OPEN_OVERLAY',
  UPDATE_BACK_ACTION: 'UPDATE_BACK_ACTION',
  UPDATE_OVERLAY: 'UPDATE_OVERLAY'
};

/**
 * @function overlayProviderReducer
 * @description Manages OverlayConfig/state
 *
 * @param {object} state Reducer state
 * @param {object} action Reducer action
 * @returns {OverlayConfig}
 */
export const overlayProviderReducer = ( state, action ) => {
  switch ( action.type ){
    case OVERLAY_ACTIONS.OPEN_OVERLAY:
      return utils.handleOpenOverlay( { state: { ...configInitialState, ...state }, action } );

    case OVERLAY_ACTIONS.CLOSE_OVERLAY:
      return { ...state, ...configInitialState };

    case OVERLAY_ACTIONS.UPDATE_OVERLAY:
    case OVERLAY_ACTIONS.UPDATE_BACK_ACTION:
      return { ...state, ...action.payload };
  }
};

/**
 * @function useDevMode
 * @description useDevMode helper to display DevPanel
 * @returns {boolean}
 */
export const useDevMode = () => {
  const [devMode, setDevMode] = useState(
    !isServer() && !!window.__ULTA_DEBUG_MODE__
  );

  useEffect( () => {
    if( !isServer() ){
      const handleKeyDown = ( e ) => {
        const urlParams = new URLSearchParams( window.location.search );
        const isDevModeParam = urlParams.get( 'DEV_MODE' ) === 'true';

        // Hotkey: Ctrl + Alt + D (or Ctrl + Option + D on Mac)
        // Only work if DEV_MODE=true is in the query params
        if( isDevModeParam && e.altKey && ( e.ctrlKey || e.metaKey ) && e.code === 'KeyD' ){
          e.preventDefault();
          const next = !window.__ULTA_DEBUG_MODE__;
          window.__ULTA_DEBUG_MODE__ = next;
          window.dispatchEvent( new CustomEvent( 'ulta:dev-mode-change', { detail: next } ) );
        }
      };

      const handleDevModeChange = ( e ) => {
        setDevMode( !!e.detail );
      };

      window.addEventListener( 'keydown', handleKeyDown );
      window.addEventListener( 'ulta:dev-mode-change', handleDevModeChange );

      return () => {
        window.removeEventListener( 'keydown', handleKeyDown );
        window.removeEventListener( 'ulta:dev-mode-change', handleDevModeChange );
      };
    }
  }, [] );

  return devMode;
};

/**
 * @function handleOpenOverlay
 * @description Reducer helper for handeling overlay open state
 *
 * @param {object} data arguments
 * @param {object} data.state Reducer state
 * @param {object} data.action Reducer action
 * @returns {OverlayConfig}
 */
export const handleOpenOverlay = ( data ) => {
  const { state, action: { payload = {} } = {} } = handleEmptyObjects( data );

  const {
    action,
    alignment,
    ariaLabel,
    backAction,
    backActionCallback,
    closeAccessibilityLabel,
    content,
    crossButtonVisibility,
    customClassName,
    disableClose,
    isSwatchModal,
    isVideo,
    closeAction,
    onClose,
    opener,
    offsetElement,
    type,
    videoMeta,
    title,
    subtitle,
    headerText,
    customCloseEvent,
    renderAsModalOnAllViewports,
    heightPercentageOverride
  } = payload;

  const openOverlayConfig = {
    alignment,
    ariaLabel,
    backAction,
    backActionCallback,
    closeAccessibilityLabel,
    crossButtonVisibility,
    customClassName,
    disableClose,
    displayOverlay: true,
    isSwatchModal,
    isVideo,
    onClose,
    closeAction,
    opener,
    offsetElement,
    type,
    videoMeta,
    title,
    subtitle,
    headerText,
    customCloseEvent,
    renderAsModalOnAllViewports,
    heightPercentageOverride
  };

  if( content ){
    openOverlayConfig.content = content;
  }
  else if( action ){
    openOverlayConfig.showDynamicFlyoutHeader = action.navigationType === DXL_NAVIGATION_TYPE.Flyout;
    openOverlayConfig.content = (
      <DynamicOverlayComponent action={ action } />
    );
  }

  return { ...state, ...openOverlayConfig };
};

/**
 * @function isOverlay
 * @description Returns whether the given type is a valid overlay type.
 * @param {string} type
 * @returns {boolean}
 */
export const isOverlay = ( type ) => {
  return [OVERLAY_TYPE.Modal, OVERLAY_TYPE.Flyout, OVERLAY_TYPE.BottomSheet].includes( type );
};

/**
 * @function getOverlayType
 * @description Returns overlay type.
 * @param {object} type
 * @returns {object}
 */
export const getOverlayType = ( { displayOverlay, type, breakpoint, isVideo, renderAsModalOnAllViewports } ) => {
  const isMobileDevice = breakpoint?.CURRENT_BREAKPOINT === 'SM';

  let displayBottomSheet = displayOverlay && type === OVERLAY_TYPE.BottomSheet && isMobileDevice;
  let displayModal = displayOverlay && type === OVERLAY_TYPE.Modal;
  let displayFlyout = displayOverlay && type === OVERLAY_TYPE.Flyout;

  // Bottom Sheet should only show for mobile devices, otherwise render in a traditional flyout
  if( type === OVERLAY_TYPE.BottomSheet && !isMobileDevice ){
    displayFlyout = true;
  }
  // Non Video Modal should render as bottomSheet if opened in mobile device
  // Video modal will still be rendered as modal irrespective of device inflection
  if( displayModal && !isVideo && isMobileDevice && !renderAsModalOnAllViewports ){
    displayBottomSheet = true;
    displayModal = false;
  }

  return {
    displayBottomSheet,
    displayFlyout,
    displayModal
  };
};

/**
 * @function getBackdropClassName
 * @description Helper to determine the backdrop class name.
 * @param {object} data
 * @returns {string}
 */
export const getBackdropClassName = ( data ) => {
  const { type, isClosing, customClassName } = data || {};
  const isBottomSheet = type === OVERLAY_TYPE.BottomSheet;
  const isModal = type === OVERLAY_TYPE.Modal;
  const isFlyout = type === OVERLAY_TYPE.Flyout;

  return classNames( {
    'OverlayBottomSheet__backdrop': isBottomSheet,
    'OverlayModal--backdrop': isModal,
    'OverlayFlyout--backdrop': isFlyout,
    'OverlayBottomSheet__backdrop--closing': isClosing && isBottomSheet,
    ...( customClassName && {
      [ `OverlayBottomSheet--${customClassName}` ]: isBottomSheet,
      [ `OverlayModal--${customClassName}` ]: isModal,
      [ `OverlayFlyout--backdrop--UtilityLinks` ]: isFlyout,
      [ `OverlayFlyout--${customClassName}` ]: isFlyout
    } )
  } );
};

/**
 * @const {string} OVERLAY_SCROLL_BLOCK_CLASS - Overlay scroll block class
 */
export const OVERLAY_SCROLL_BLOCK_CLASS = 'Overlay-Scroll-Block';

/**
 * @const {string} OVERLAY_CLOSE_EVENT - Overlay close event
 */
export const OVERLAY_CLOSE_EVENT = 'OVERLAY_CLOSE_EVENT';

/**
 * @typedef {object} OVERLAY_TYPE
 * @property {'flyout'} Flyout
 * @property {'bottomSheet'} BottomSheet
 * @property {'modal'} Modal
 */

/**
 * @type {OVERLAY_TYPE}
 */
export const OVERLAY_TYPE = {
  Flyout: 'flyout',
  Modal: 'modal',
  BottomSheet: 'bottomsheet'
};

/**
 * @typedef {object} OVERLAY_ALIGNMENT
 * @property {'left'} Left
 * @property {'right'} right
 */

/**
 * @type {OVERLAY_ALIGNMENT}
 */
export const OVERLAY_ALIGNMENT = {
  Left: 'left',
  Right: 'right'
};

/**
 * @typedef {object} openOverlayOptions
 * @property {string} ariaLabel Overlay ariaLabel
 * @property {OVERLAY_TYPE} type Overlay type
 * @property {{ current: object }} opener Open element's React Ref
 * @property {JSX.Element} content React content to display in the overlay
 * @property {boolean} isVideo
 * @property {{ width: number, height: number }} videoMeta Video dimensions meta
 * @property {OVERLAY_ALIGNMENT} alignment
 * @property {{ label: string }} closeAccessibilityLabel ADA label
 * @property {function} onClose called on close
 * @property {current} offsetElement Ref of Element to get top offset from
 * @property {object} action DXL Action
 * @property {boolean} isSwatchModal used for swatch modal, should be deprecated
 * @property {boolean} [crossButtonVisibility=true] show cross button
 * @property {string} customClassName sets a class name on the dialog
 * @property {string} renderAsModalOnAllViewports sets a class name on the dialog
 */

/**
 * @callback openOverlay
 * @param {openOverlayOptions} options Overlay opener arguments
 */

/**
 * @callback updateContent
 * @param {JSX.Element} content Update the overlay content
 */

/**
 * @callback updateOverlay
 * @param {JSX.Element} content Update the overlay props
 */

/**
 * @callback updateBackAction
 * @param {object} data - Arguments
 * @param {object} data.backAction - DXL action
 * @param {object} methods - Methods
 * @param {function} methods.backActionCallback - On back action click
 */

/**
 * @callback updateCloseAction
 * @param {object} data - Arguments
 * @param {object} data.action - DXL close action
 * @param {boolean} data.loading - DXL loading state
 * @param {object} methods - Methods
 * @param {function} methods.invokeAction - Layer host invokeAction
 * @param {function} methods.invokeMutation - Layer host invokeMutation
 * @param {function} methods.onBeforeClose - onBeforeClose is invoked before the action is dispatched
 * @param {function} methods.onAfterClose - onAfterClose is invoked after the action is done loading
 * @returns {Promise} Will resolve when loading is done
 */

/**
 * @typedef {object} OverlayContext
 * @property {openOverlay} openOverlay Open overlay function
 * @property {function} closeOverlay Close overlay function
 * @property {updateContent} updateContent Update the overlay content
 * @property {updateOverlay} updateOverlay Update the overlay props
 * @property {updateBackAction} updateBackAction Update the overlay header back action
 * @property {updateCloseAction} updateCloseAction Update the overlay close back action
 * @property {function} showConfetti Triggers a confetti burst. Accepts props for the Confetti component.
 * @property {function} hideConfetti Hides/removes the confetti.
 * @property {handleOverlayCloseIcon} handleOverlayCloseIcon Event handler for 'X' icon
 */
export const OverlayContext = createContext( {} );

/**
 * @method
 * @returns {OverlayContext}
 */
export const useOverlay = () => useContext( OverlayContext );

export default OverlayProvider;

